import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { initialize, LDClient, LDFlagSet } from 'launchdarkly-js-client-sdk';

import { AppConfigService } from '../services/app-config.service';
import { AZURE_DOCUMENT_INTELLIGENCE_EARLY_CLASSIFICATION_UI_FLAG } from '../utils/constants';
import { CommonFunctions } from '../utils/common-functions';
import { User } from '@models/user';
import { LaunchDarklyIdentifyDiagnostics } from '@models/launchdarkly-identify-diagnostics';

// @TechDebt: azure_document_intelligence is hard-coded on and never read from LaunchDarkly.
// Remove this override (and the flag checks that read it) once the legacy flow is deleted.
const HARDCODED_FLAGS: LDFlagSet = {
  [AZURE_DOCUMENT_INTELLIGENCE_EARLY_CLASSIFICATION_UI_FLAG]: true,
};

@Injectable()
export class LaunchDarklyService {
  ldClient: LDClient;
  flags: LDFlagSet;
  flagChange: Subject<Object> = new Subject<Object>();

  // Emits `true` once the LaunchDarkly client has identified the user (the
  // per-tenant context) and the identified flag set is populated. Consumers
  // that read a targeted flag at a single point in time (e.g. a route guard)
  // wait for this so they never read the anonymous bootstrap flags.
  // NOTE: this is intentionally driven by `identify()` resolving in `setUser()`,
  // NOT by the client `ready` event — `ready` fires for the anonymous bootstrap
  // context, where per-user/per-tenant target rules do not yet apply.
  private _flagsReady$ = new BehaviorSubject<boolean>(false);
  flagsReady$: Observable<boolean> = this._flagsReady$.asObservable();

  // Incremented per identify() call so a slower, older identify cannot
  // overwrite the flags of a newer one when responses resolve out of order.
  private _latestIdentifyId = 0;

  // Last identify() state, read by flag-gated guards to log WHY a flag read
  // fell back (they cannot distinguish "slow", "failed" and "no tenant" otherwise).
  // The logger is not injected here because LoggingService depends on this service.
  identifyDiagnostics: LaunchDarklyIdentifyDiagnostics = {
    status: 'none',
    identifyCalls: 0,
    hasBankId: false,
    isAnonymous: true,
  };

  constructor(private _appConfigService: AppConfigService) {
    // defaults
    this.flags = { ...HARDCODED_FLAGS };

    this.ldClient = initialize(CommonFunctions.getLaunchDarklyClientId(),
      { key: 'SAMPLE-USER-KEY', anonymous: true },
      { bootstrap: 'localStorage', fetchGoals: false }
    );

    this.ldClient.on('change', (flags) => {
      for (const key in flags) {
          // skip loop if the property is from prototype
          if (!flags.hasOwnProperty(key)) continue;

          this.flags[key] = flags[key].current;
      }
      this.applyHardcodedFlags();
      this.flagChange.next(this.flags);
   });

   this.ldClient.on('ready', () => {
     // Populate the anonymous bootstrap flags so direct reads of `this.flags`
     // and `flagChange` subscribers still work, but do NOT signal readiness:
     // `flagsReady$` must reflect the identified (per-tenant) context, which is
     // only available after `identify()` resolves in `setUser()`. Signalling
     // readiness here let flag-gated routes read the anonymous flag set on a
     // hard refresh (see DataCategorizationRedesignMatch).
     this.setFlags();
   })
  }

  setFlags() {
    this.flags = this.ldClient.allFlags();
    this.applyHardcodedFlags();
    this.flagChange.next(this.flags);
  }

  setUser(user) {
    // Identify the per-tenant context and signal readiness once the identified
    // flag set resolves. Flag-gated route guards (e.g. DataCategorizationRedesignMatch)
    // wait on `flagsReady$` for exactly this, and trigger identification
    // themselves so the flags are loaded before a flag-gated route is matched.
    const identifyId = ++this._latestIdentifyId;
    const startedAt = Date.now();
    // The flags held now belong to the previous identify; guards must wait for this one.
    this._flagsReady$.next(false);
    this.identifyDiagnostics = {
      status: 'pending',
      identifyCalls: identifyId,
      hasBankId: !!user.bank_id,
      isAnonymous: !user.user_sub,
    };
    this.ldClient.identify(this._claimsToLdUser(user))
      .then((flags) => {
        if (identifyId !== this._latestIdentifyId) {
          return;
        }
        this.identifyDiagnostics = {
          ...this.identifyDiagnostics,
          status: 'resolved',
          elapsedMs: Date.now() - startedAt,
        };
        this.flags = flags;
        this.applyHardcodedFlags();
        this.flagChange.next(this.flags);
        this._flagsReady$.next(true);
      })
      .catch((error: unknown) => {
        if (identifyId !== this._latestIdentifyId) {
          return;
        }
        this.identifyDiagnostics = {
          ...this.identifyDiagnostics,
          status: 'failed',
          elapsedMs: Date.now() - startedAt,
          errorMessage: error instanceof Error ? error.message : String(error),
        };
        // identify() failed: keep the last-known flags, but still signal
        // readiness so flag-gated guards resolve immediately (reading the
        // best-known flags) instead of stalling for the full timeout — and so a
        // single failure does not poison every later navigation via the cached
        // user (`getCurrentUser()` would not retry `setUser`). The best-known
        // flags are the safe (legacy) values when identification is unavailable.
        this._flagsReady$.next(true);
      });
  }

  private applyHardcodedFlags(): void {
    Object.assign(this.flags, HARDCODED_FLAGS);
  }

  _claimsToLdUser(userClaims: User) {
    const key = userClaims.user_sub || 'anon';
    const userIdentification: Record<string, unknown> = {
      key,
      anonymous: !userClaims.user_sub,
      email: userClaims.user_email
    };
    if (!!userClaims.bank_id) {
      userIdentification['custom'] = { 'bank_id': userClaims.bank_id };
    }
    return userIdentification;
  }

  setAnonymous() {
    this.ldClient.identify({key: 'anon', anonymous: true});
  }
}
